import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Box, Typography, IconButton, Tooltip } from "@mui/material";
import {
  RefreshCw,
  Users,
  UserCheck,
  Wifi,
  WifiOff,
  Clock,
  UserX,
  PauseCircle,
} from "react-feather";
import useDashboard from "../../../../hooks/useDashboard";
import useAuth from "../../../../hooks/useAuth";
import useTenant from "../../../../hooks/useTenant";
import useUser from "../../../../hooks/useUser";
import useDailyUser from "../../../../hooks/useDailyUser";
import useAuthenticationLog from "../../../../hooks/useAuthenticationLog";
import { formatDate, addDays } from "../../../../utils/common";
import ProCard from "../ProCard";
import ProBarChart from "../ProBarChart";
import ProLineChart from "../ProLineChart";
import InvoiceStatsWidget from "../InvoiceStatsWidget";

const STAT_CONFIG = [
  {
    key: "total",
    label: "Total",
    route: "/user/userlist/list",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    Icon: Users,
  },
  {
    key: "active",
    label: "Active",
    route: "/user/active",
    color: "#3b82f6",
    bg: "#eff6ff",
    Icon: UserCheck,
  },
  {
    key: "online",
    label: "Online",
    route: "/user/online",
    color: "#22c55e",
    bg: "#f0fdf4",
    Icon: Wifi,
  },
  {
    key: "offline",
    label: "Offline",
    route: "/user/offline",
    color: "#64748b",
    bg: "#f8fafc",
    Icon: WifiOff,
  },
  {
    key: "expired",
    label: "Expired",
    route: "/user/expired",
    color: "#f97316",
    bg: "#fff7ed",
    Icon: Clock,
  },
  {
    key: "suspended",
    label: "Suspended",
    route: "/user/suspended",
    color: "#ef4444",
    bg: "#fef2f2",
    Icon: UserX,
  },
  {
    key: "onHold",
    label: "On Hold",
    route: "/user/hold",
    color: "#eab308",
    bg: "#fefce8",
    Icon: PauseCircle,
  },
];

function StatTile({ label, value, color, bg, Icon, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: "16px",
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : bg),
        border: `1px solid ${color}20`,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 24px ${color}20`,
          border: `1px solid ${color}40`,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1, mb: 0.75 }}
          >
            {value.toLocaleString()}
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            backgroundColor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={18} color="#fff" strokeWidth={2.5} />
        </Box>
      </Box>
    </Box>
  );
}

const DefaultUserOverView = () => {
  const { getDashboardUserCount, dashboadUserCount, isUserDashboardLoading } = useDashboard();
  const { getAllDashboardUser, dashboardUsers } = useDailyUser();
  const { getAuthStats, authenticationLogStats } = useAuthenticationLog();
  const { user } = useAuth();
  const { getTenant } = useTenant();
  const { getExpiringUserCounts } = useUser();
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  const [expiringCounts, setExpiringCounts] = useState({
    yesterday: 0,
    today: 0,
    tomorrow: 0,
    next2Days: 0,
  });

  const fetchData = async () => {
    if (!user) return;
    const commonFilters = [];

    const dashboardPayload = {
      filters: commonFilters,
      pagination: { pageNumber: 1, pageSize: 100 },
      sortFields: [],
    };

    await Promise.all([
      getDashboardUserCount(dashboardPayload),
      getAllDashboardUser(dashboardPayload),
    ]);

    if (user.businessType !== "SUPER_ADMIN") {
      getTenant(user.tenantId);
    }

    const counts = await getExpiringUserCounts({});
    if (counts) {
      setExpiringCounts({
        yesterday: counts.yesterdayCount || 0,
        today: counts.todayCount || 0,
        tomorrow: counts.tomorrowCount || 0,
        next2Days: counts.next2DaysCount || 0,
      });
    }
  };

  useEffect(() => {
    if (!user || hasFetched.current) return;
    fetchData();
    hasFetched.current = true;
  }, [user, getDashboardUserCount, getTenant]);

  if (isUserDashboardLoading || !dashboadUserCount) {
    return <Typography>Loading Dashboard...</Typography>;
  }

  const {
    totalCount = 0,
    activeCount = 0,
    onlineCount = 0,
    expireCount = 0,
    suspendedCount = 0,
    onHoldCount = 0,
  } = dashboadUserCount;

  const offlineCount = dashboadUserCount.offlineCount || 0;

  const statValues = {
    total: totalCount,
    active: activeCount,
    online: onlineCount,
    offline: offlineCount,
    expired: expireCount,
    suspended: suspendedCount,
    onHold: onHoldCount,
  };

  const handleBarClick = (label) => {
    switch (label) {
      case "Total":
        navigate("/user/userlist/list");
        break;
      case "Active":
        navigate("/user/active");
        break;
      case "Online":
        navigate("/user/online");
        break;
      case "Offline":
        navigate("/user/offline");
        break;
      case "Expired":
        navigate("/user/expired");
        break;
      case "Suspended":
        navigate("/user/suspended");
        break;
      case "On Hold":
        navigate("/user/hold");
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary" }}>
            Dashboard Insights
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Real-time connection overview
          </Typography>
        </Box>
        <Tooltip title="Refresh all">
          <IconButton
            size="small"
            onClick={() => fetchData()}
            sx={{
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.15)"
                  : "1px solid #e2e8f0",
              borderRadius: "8px",
              color: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
                borderColor: "#3b82f6",
                color: "#3b82f6",
                transform: "rotate(180deg)",
              },
            }}
          >
            <RefreshCw size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stat tiles */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CONFIG.map(({ key, label, route, color, bg, Icon }) => (
          <Grid item xs={6} sm={4} md={12 / 7} key={key}>
            <StatTile
              label={label}
              value={statValues[key]}
              color={color}
              bg={bg}
              Icon={Icon}
              onClick={() => navigate(route)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Charts row — Connection distribution and Invoice Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <ProCard title="Connection distribution">
            <ProBarChart
              total={totalCount}
              active={activeCount}
              online={onlineCount}
              offline={offlineCount}
              expired={expireCount}
              suspended={suspendedCount}
              onHold={onHoldCount}
              onBarClick={handleBarClick}
            />
          </ProCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InvoiceStatsWidget />
        </Grid>
      </Grid>

      {/* Authorizations chart has been moved to last dashboard row next to balance sheet */}
    </Box>
  );
};

export default DefaultUserOverView;
